import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  Activity, 
  Brain, 
  Heart, 
  Shield, 
  Sparkles, 
  ArrowRight,
  Play,
  CheckCircle2
} from 'lucide-react';

const HeroSection = () => {
  return (
    <section className="relative min-h-[90vh] bg-[#0a1f13] overflow-hidden">
      {/* Animated Background Grid */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(22,101,52,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(22,101,52,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-bl from-[#166534]/20 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-[#22c55e]/10 via-transparent to-transparent rounded-full blur-3xl" />
      </div>

      {/* Floating Elements */}
      <div className="absolute top-20 right-20 w-20 h-20 bg-gradient-to-br from-[#22c55e]/20 to-[#166534]/20 rounded-2xl backdrop-blur-sm border border-white/5 flex items-center justify-center animate-pulse">
        <Heart className="w-8 h-8 text-[#22c55e]" />
      </div>
      <div className="absolute top-40 right-60 w-16 h-16 bg-gradient-to-br from-[#3b82f6]/20 to-[#1d4ed8]/20 rounded-2xl backdrop-blur-sm border border-white/5 flex items-center justify-center" style={{ animation: 'pulse 3s infinite 0.5s' }}>
        <Brain className="w-6 h-6 text-[#3b82f6]" />
      </div>
      <div className="absolute bottom-40 right-32 w-14 h-14 bg-gradient-to-br from-[#f59e0b]/20 to-[#d97706]/20 rounded-2xl backdrop-blur-sm border border-white/5 flex items-center justify-center" style={{ animation: 'pulse 3s infinite 1s' }}>
        <Activity className="w-5 h-5 text-[#f59e0b]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm rounded-full border border-white/10">
              <Sparkles className="w-4 h-4 text-[#22c55e]" />
              <span className="text-sm text-gray-300">AI-Powered Health Platform</span>
              <span className="w-2 h-2 bg-[#22c55e] rounded-full animate-pulse" />
            </div>

            {/* Main Heading */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1]">
              Your Health,
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#22c55e] via-[#4ade80] to-[#86efac]">
                Reimagined
              </span>
              <br />
              by AI
            </h1>

            {/* Description */}
            <p className="text-lg text-gray-400 max-w-xl leading-relaxed">
              Experience the future of healthcare with our AI-powered platform. Get personalized health insights, 
              track your vitality score, and consult with virtual doctors — all in one place.
            </p>

            {/* Feature Pills */}
            <div className="flex flex-wrap gap-3">
              {[
                { icon: Shield, text: 'AHPRA/HIPAA Compliant' },
                { icon: Activity, text: 'Real-time Tracking' },
                { icon: Brain, text: 'AI Digital Twin' }
              ].map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10"
                >
                  <feature.icon className="w-4 h-4 text-[#22c55e]" />
                  <span className="text-sm text-gray-300">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap items-center gap-4 pt-4">
              <Link to="/health">
                <Button className="bg-[#22c55e] hover:bg-[#16a34a] text-black font-semibold px-8 py-6 text-base rounded-xl transition-all duration-300 hover:shadow-[0_0_40px_rgba(34,197,94,0.3)] group">
                  Start Health Tracking
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/team">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 px-8 py-6 text-base rounded-xl group">
                  <Play className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                  Meet AI Doctors
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-8 pt-8 border-t border-white/10">
              <div>
                <p className="text-3xl font-bold text-white">10K+</p>
                <p className="text-sm text-gray-500">Users</p>
              </div>
              <div className="w-px h-12 bg-white/10" />
              <div>
                <p className="text-3xl font-bold text-white">9</p>
                <p className="text-sm text-gray-500">AI Specialists</p>
              </div>
              <div className="w-px h-12 bg-white/10" />
              <div>
                <p className="text-3xl font-bold text-white">24/7</p>
                <p className="text-sm text-gray-500">Availability</p>
              </div>
            </div>
          </div>

          {/* Right Content - Dashboard Preview */}
          <div className="relative hidden lg:block">
            {/* Main Dashboard Card */}
            <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-6 shadow-2xl">
              {/* Vitality Score */}
              <div className="bg-[#0f2a1a] rounded-2xl p-6 mb-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-400 text-sm">Your Vitality Score</span>
                  <span className="text-[#22c55e] text-sm flex items-center gap-1">
                    <Activity className="w-4 h-4" />
                    +5 this week
                  </span>
                </div>
                <div className="flex items-center gap-6">
                  <div className="relative w-32 h-32">
                    <svg className="transform -rotate-90" width="128" height="128">
                      <circle cx="64" cy="64" r="56" fill="none" stroke="#1f3d2a" strokeWidth="10" />
                      <circle 
                        cx="64" cy="64" r="56" fill="none" 
                        stroke="url(#gradient)" strokeWidth="10" 
                        strokeLinecap="round"
                        strokeDasharray="351.86"
                        strokeDashoffset="77.4"
                      />
                      <defs>
                        <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#22c55e" />
                          <stop offset="100%" stopColor="#4ade80" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-4xl font-bold text-white">78</span>
                      <span className="text-xs text-gray-500">/ 100</span>
                    </div>
                  </div>
                  <div className="space-y-3 flex-1">
                    {[
                      { label: 'Sleep', value: 82, color: '#8B5CF6' },
                      { label: 'Activity', value: 75, color: '#22C55E' },
                      { label: 'Nutrition', value: 71, color: '#F59E0B' }
                    ].map((item, i) => (
                      <div key={i}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-400">{item.label}</span>
                          <span className="text-white">{item.value}</span>
                        </div>
                        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all duration-1000"
                            style={{ width: `${item.value}%`, backgroundColor: item.color }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[
                  { icon: Heart, label: 'Heart Rate', value: '72 bpm', color: '#ef4444' },
                  { icon: Activity, label: 'Steps', value: '8,542', color: '#22c55e' },
                  { icon: Brain, label: 'Bio Age', value: '31 yrs', color: '#3b82f6' }
                ].map((stat, i) => (
                  <div key={i} className="bg-[#0f2a1a] rounded-xl p-3 text-center">
                    <stat.icon className="w-5 h-5 mx-auto mb-1" style={{ color: stat.color }} />
                    <p className="text-white font-semibold text-sm">{stat.value}</p>
                    <p className="text-gray-500 text-xs">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* AI Insight */}
              <div className="bg-gradient-to-r from-[#166534]/30 to-[#22c55e]/20 rounded-xl p-4 border border-[#22c55e]/20">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#22c55e] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-black" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium mb-1">AI Health Insight</p>
                    <p className="text-gray-400 text-xs">Your sleep quality improved 15% this week. Keep maintaining your bedtime routine!</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Doctor Card - removed */}
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
